import { observer } from "mobx-react";
import "./Profile.scss";
import useTranslation from "../../hooks/useTranslation";
import { Controller, useForm } from "react-hook-form";
import { User, UserRoles } from "../../stores/AuthStore/types";
import React, { useEffect, useState } from "react";
import authStore from "../../stores/AuthStore";
import {
  Button,
  Checkbox,
  CircularProgress,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import profileFormRules from "./rules";
import feedbackStore from "../../stores/FeedbackStore";
import { FeedbackSeverity } from "../../stores/FeedbackStore/types";
import Box from "@mui/material/Box";
import { LawyerFields } from "./LawyerFields";
import CodeValidation from "./CodeValidation";
import ConfirmDialog from "../../components/ConfirmDialog";

interface ProfileProps {
  controlledUser?: User;
  afterUpdateUser?: (user: User) => void;
}

const Profile = observer(
  ({ controlledUser, afterUpdateUser }: ProfileProps) => {
    const { t } = useTranslation();
    const [hasSubmit, setHasSubmit] = useState(false);
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
    const isAdmin = authStore.userHasRole(UserRoles.ADMIN);
    const {
      control,
      formState: { errors },
      handleSubmit,
      setValue,
      getValues,
      watch,
      trigger,
    } = useForm<User>({
      defaultValues: {
        barName: "",
      },
    });
    const codesAreValid = isAdmin || watch("emailValid");

    useEffect(() => {
      const data = controlledUser || authStore.user;
      setValue("id", data.id);
      setValue("email", data.email);
      setValue("emailValid", data.emailValid);
      setValue("phone", data.phone);
      setValue("phoneValid", data.phoneValid);
      setValue("firstName", data.firstName);
      setValue("lastName", data.lastName);
      setValue("roles", data.roles || []);

      if (
        authStore.userHasRole(UserRoles.USER) ||
        authStore.userHasRole(UserRoles.LAWYER) ||
        authStore.userHasRole(UserRoles.ADMIN)
      ) {
        setValue("barNo", data.barNo);
        setValue("barName", data.barName);
        setValue("barStatus", data.barStatus);
      }
    }, [controlledUser, setValue]);

    const onSubmit = (data: User) => {
      setHasSubmit(true);

      authStore
        .updateUser(data)
        .then((result) => {
          if (data.id === authStore.user.id) {
            authStore.user = {
              ...result,
              emailValid: true,
              phoneValid: true,
            };
          }

          // Invoke after event if defined
          afterUpdateUser && afterUpdateUser(data);

          feedbackStore.setShowFeedback(true, {
            severity: FeedbackSeverity.SUCCESS,
            message: "user-saved",
          });
        })
        .catch(() => {
          feedbackStore.setShowFeedback(true, {
            severity: FeedbackSeverity.ERROR,
            message: "user-save-failed",
          });
        })
        .finally(() => setHasSubmit(false));
    };

    const form = (
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{ display: { xs: "block", sm: "flex" } }}
      >
        <form
          onSubmit={handleSubmit(onSubmit)}
          className={controlledUser ? "controlledForm" : ""}
        >
          <Box>
            <Controller
              name={"id"}
              control={control}
              render={() => (
                <>
                  <h4 className="mt0 mb0">{t("user-id")}:</h4>
                  <p>{getValues("id")}</p>
                </>
              )}
            />

            <Controller
              name={"email"}
              control={control}
              rules={profileFormRules.email}
              render={({ field: { onChange, value } }) => (
                <>
                  <h4>{t("email")}:</h4>
                  <TextField
                    error={!!errors.email}
                    placeholder={t("please-write-here")}
                    rows={1}
                    size={"small"}
                    onChange={(e) => {
                      onChange(e);
                      trigger("email").then(() => {
                        setValue("emailValid", false);
                      });
                    }}
                    value={value}
                  />
                  <div className="error mt mb">
                    {errors.email && t(`profile-email-${errors.email.type}`)}
                  </div>
                  {!isAdmin && !watch("emailValid") && !errors.email && (
                    <CodeValidation
                      inputLabel={"enter-code"}
                      sendCodeLabel={"send-code"}
                      validateCodeLabel={"validate-code"}
                      value={getValues("emailValid")}
                      setValue={(value) => setValue("emailValid", value)}
                      sendCode={() =>
                        authStore.sendEmailCode(getValues("email"))
                      }
                      validateCode={(code) => authStore.validateEmailCode(code)}
                    />
                  )}
                </>
              )}
            />

            <Controller
              name={"phone"}
              control={control}
              rules={profileFormRules.phone}
              render={({ field: { onChange, value } }) => (
                <>
                  <h4>{t("phone-number")}:</h4>
                  <TextField
                    error={!!errors.phone}
                    placeholder={t("please-write-here")}
                    rows={1}
                    size={"small"}
                    onChange={(e) => {
                      onChange(e);
                      trigger("phone").then(() => {
                        setValue("phoneValid", false);
                      });
                    }}
                    value={value}
                  />
                  <div className="error mt">
                    {errors.phone && t(`profile-phone-${errors.phone.type}`)}
                  </div>
                  {/*!isAdmin && !watch("phoneValid") && !errors.phone && (
                  <CodeValidation
                    inputLabel={"enter-code"}
                    sendCodeLabel={"send-code"}
                    validateCodeLabel={"validate-code"}
                    value={getValues("phoneValid")}
                    setValue={(value) => setValue("phoneValid", value)}
                    sendCode={() => authStore.sendPhoneCode()}
                    validateCode={(code) => authStore.validatePhoneCode(code)}
                  />
                )*/}
                </>
              )}
            />

            <Controller
              name={"firstName"}
              control={control}
              rules={profileFormRules.firstName}
              render={({ field: { onChange, value } }) => (
                <>
                  <h4>{t("first-name")}:</h4>
                  <TextField
                    error={!!errors.firstName}
                    placeholder={t("please-write-here")}
                    rows={1}
                    size={"small"}
                    onChange={(e) => {
                      onChange(e);
                      trigger("firstName").then();
                    }}
                    value={value}
                  />
                  <div className="error mt">
                    {errors.firstName &&
                      t(`profile-first-name-${errors.firstName.type}`)}
                  </div>
                </>
              )}
            />

            <Controller
              name={"lastName"}
              control={control}
              rules={profileFormRules.lastName}
              render={({ field: { onChange, value } }) => (
                <>
                  <h4>{t("last-name")}:</h4>
                  <TextField
                    error={!!errors.phone}
                    placeholder={t("please-write-here")}
                    rows={1}
                    size={"small"}
                    onChange={(e) => {
                      onChange(e);
                      trigger("lastName").then();
                    }}
                    value={value}
                  />
                  <div className="error mt">
                    {errors.lastName &&
                      t(`profile-last-name-${errors.lastName.type}`)}
                  </div>
                </>
              )}
            />

            {!!controlledUser && (
              <>
                <Controller
                  name={"roles"}
                  control={control}
                  rules={profileFormRules.roles}
                  render={({ field: { onChange, value } }) => (
                    <>
                      <InputLabel id="user-roles-label">
                        {t("user-roles")}
                      </InputLabel>
                      <Select
                        fullWidth
                        multiple
                        labelId="user-roles-label"
                        className="mt"
                        error={!!errors.roles}
                        value={Array.isArray(value) ? value : []}
                        renderValue={(selected) =>
                          selected
                            .map((choice: UserRoles) => t(choice.toLowerCase()))
                            .join(",")
                        }
                        onChange={(e) => {
                          onChange(e);
                          trigger("roles");
                        }}
                      >
                        {Object.values(UserRoles).map((item) => (
                          <MenuItem key={item} value={item}>
                            <Checkbox checked={value?.includes(item)} />
                            <ListItemText primary={t(item.toLowerCase())} />
                          </MenuItem>
                        ))}
                      </Select>
                      <div className="error mt">
                        {errors.roles &&
                          t(`profile-roles-${errors.roles.type}`)}
                      </div>
                    </>
                  )}
                />
                {authStore.userHasRole(UserRoles.ADMIN) &&
                  watch("roles")?.includes(UserRoles.LAWYER) && (
                    <div className="mt">
                      <LawyerFields
                        user={controlledUser}
                        parentForm={{ trigger, setValue }}
                      />
                    </div>
                  )}
              </>
            )}
          </Box>
          <footer className="mt pt">
            <div className="mb">
              <Button
                type="submit"
                variant={"contained"}
                fullWidth={true}
                disabled={
                  hasSubmit ||
                  Object.values(errors).some((error) => error) ||
                  !codesAreValid
                }
              >
                {hasSubmit ? <CircularProgress size={"16px"} /> : t("save")}
              </Button>
            </div>
            <div>
              <Button
                className="mt"
                variant="outlined"
                color="error"
                fullWidth={true}
                onClick={() => setIsConfirmDialogOpen(true)}
              >
                <i className="fal fa-user-slash" />
                &nbsp;&nbsp;
                {t("deactivate-user")}
              </Button>
            </div>
          </footer>
        </form>

        {(authStore.userHasRole(UserRoles.USER) ||
          authStore.userHasRole(UserRoles.LAWYER)) && (
          <LawyerFields user={getValues()} />
        )}
      </Stack>
    );

    return (
      <div className="profileContainer container">
        {!controlledUser && (
          <Button
            className="hidden-tablet logout"
            variant="outlined"
            color="warning"
            onClick={() => authStore.logout().then()}
          >
            <i className="fal fa-power-off" />
            &nbsp;&nbsp;
            {t("logout")}
          </Button>
        )}

        {form}

        <ConfirmDialog
          text={t("deactivate-user-info")}
          title={t("deactivate-user")}
          truthyColor="error"
          truthyText={t("confirm")}
          falsyText={t("cancel")}
          successText={t("user-deactivated")}
          errorText={t("user-deactivate-failed")}
          dialogOpen={isConfirmDialogOpen}
          setDialogOpen={setIsConfirmDialogOpen}
          onConfirm={() =>
            authStore
              .deactivateUser()
              .then(() =>
                authStore.logout().then(() => {
                  feedbackStore.setShowFeedback(true, {
                    severity: FeedbackSeverity.SUCCESS,
                    message: "user-deactivated",
                  });
                })
              )
              .catch(() => {
                feedbackStore.setShowFeedback(true, {
                  severity: FeedbackSeverity.ERROR,
                  message: "user-deactivate-failed",
                });
              })
          }
        />
      </div>
    );
  }
);

export default Profile;
